import {ApplicationConfig, inject, provideZoneChangeDetection} from '@angular/core';
import {provideRouter, withHashLocation} from '@angular/router';
import {provideNamedApollo} from 'apollo-angular';
import {routes} from './app.routes';
import {OAuthService, OAuthStorage, provideOAuthClient} from 'angular-oauth2-oidc';
import {environment} from '../environments/environment';
import {HttpLink} from 'apollo-angular/http';
import {ContextService} from './services/context.service';
import {ApolloLink, InMemoryCache, InMemoryCacheConfig, TypePolicy} from '@apollo/client/core';
import {LocalState} from '@apollo/client/local-state';
import {HttpHeaders, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {HashLocationStrategy, LocationStrategy} from '@angular/common';
import {DEFAULT_DIALOG_CONFIG} from '@angular/cdk/dialog';
import {Po2024Service} from './services/po-2024.service';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideNgIdle} from '@ng-idle/core';
import {FullscreenOverlayContainer, OverlayContainer} from '@angular/cdk/overlay';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(routes, withHashLocation()),
    provideAnimations(),
    provideNgIdle(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: LocationStrategy, useClass: HashLocationStrategy
    },
    {
      provide: OAuthStorage, useValue: localStorage
    },
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
    {
      provide: DEFAULT_DIALOG_CONFIG, useValue: {
        hasBackdrop: true,
        disableClose: true,
        width: '720px',
        'max-width': '100%',
      }
    },
    provideNamedApollo(() => {
        const httpLink = inject(HttpLink);
        const oAuthService = inject(OAuthService);
        const contextService = inject(ContextService);
        const authMiddleware = new ApolloLink((operation, forward) => {
          const accessToken = oAuthService.getAccessToken();
          let headers = operation.getContext().headers || new HttpHeaders();
          headers = headers.set('Authorization', accessToken);
//          console.log('Appsync request',  contextService.organization())
//          console.log('operation', operation.operationName)
          if (contextService.organization() && !headers.get('X-Castlabs-Organization')) {

            headers = headers.set('X-Castlabs-Organization', contextService.organization().id);
          }

          operation.setContext({
            headers: headers
          });

          return forward(operation);
        });

        function createCache(organizationService: ContextService) {
          const generateCacheKey = (args) => {
            if (organizationService.organizationId()) {
              if (!args) {
                return organizationService.organizationId();
              } else {
                return `${organizationService.organizationId()}:${JSON.stringify(args)}`;
              }
            }
            if (!args) {
              return null;
            } else {
              return JSON.stringify(args);
            }
          };
          const headerAware = {
            keyArgs: false,
            read(existing, {args}) {
              const key = generateCacheKey(args)
              return existing ? existing[key] : undefined;
            },
            merge(existing = {}, incoming, {args}) {
              const key = generateCacheKey(args);
              return {
                ...existing,
                [key]: incoming,
              };
            },
          } as TypePolicy;

          const cacheParams = {

            typePolicies: {
              Query: {
                fields: {
                  roots: headerAware,
                  airlines: headerAware,
                  policies: headerAware,
                  list_POs: headerAware,
                }
              },
              File: {
                fields: {
                  archive: {
                    merge: true,
                  },
                },
              },
              POItem: {
                // Type-level merge: reject incoming POItem poll data when:
                // 1. The existing process start_date is less than 15s old (settle window, must exceed poll interval)
                // 2. The incoming process is older than the existing process (stale poll data)
                merge(existing, incoming, {mergeObjects, readField}) {
                  if (!existing) return incoming;
                  if (!incoming) return existing;

                  const getStartDate = (processValue: any): string | undefined => {
                    if (!processValue) return undefined;
                    if (processValue.start_date) return processValue.start_date;
                    if (processValue.__ref) {
                      try { return readField('start_date', processValue) as string; } catch { return undefined; }
                    }
                    return undefined;
                  };

                  const SETTLE_MS = 15000;
                  const now = Date.now();

                  const existingWpStart = getStartDate(existing.workflow_process);
                  const incomingWpStart = getStartDate(incoming.workflow_process);
                  if (existingWpStart) {
                    if ((now - Date.parse(existingWpStart)) < SETTLE_MS) {
                      return existing;
                    }
                    if (incomingWpStart && Date.parse(incomingWpStart) < Date.parse(existingWpStart)) {
                      return existing;
                    }
                  }

                  const existingPpStart = getStartDate(existing.publish_process);
                  const incomingPpStart = getStartDate(incoming.publish_process);
                  if (existingPpStart) {
                    if ((now - Date.parse(existingPpStart)) < SETTLE_MS) {
                      return existing;
                    }
                    if (incomingPpStart && Date.parse(incomingPpStart) < Date.parse(existingPpStart)) {
                      return existing;
                    }
                  }

                  const merged = mergeObjects(existing, incoming);

                  // If publish_process was intentionally cleared (restart) and workflow_process
                  // was just updated, don't let a stale poll restore the old publish_process
                  if (!existing.publish_process && incoming.publish_process && existingWpStart) {
                    if ((now - Date.parse(existingWpStart)) < SETTLE_MS) {
                      return { ...merged, publish_process: null };
                    }
                  }

                  return merged;
                },
                fields: {
                  tracks: { merge: (_, incoming) => incoming },
                  vtk_jobs: { merge: (_, incoming) => incoming },
                  // Replaces the removed local resolver for @client field
                  format_specific_data_json: {
                    read(_, { readField }) {
                      const raw = readField('format_specific_data') as string;
                      return raw ? JSON.parse(raw) : null;
                    }
                  },
                },
              },
            },
          } as InMemoryCacheConfig;
          return new InMemoryCache(cacheParams);
        }

        return {
          'default': {
            cache: createCache(contextService),
            devtools: { enabled: true },
            link: ApolloLink.from([
              authMiddleware,
              httpLink.create({
                uri: environment.graphql
              })])
          },
          'workflow': {
            cache: createCache(contextService),
            localState: new LocalState(),
            devtools: { enabled: true },
            link: ApolloLink.from([
              authMiddleware,
              httpLink.create({
              uri: environment.workflow_graphql
            })])
          },
          // Monitoring. Its queries return aggregates over a time window, so the default cache
          // would happily serve a stale window forever -- the monitoring components fetch
          // network-only and poll instead.
          'status': {
            cache: new InMemoryCache(),
            devtools: { enabled: true },
            link: ApolloLink.from([
              authMiddleware,
              httpLink.create({
                uri: environment.status_graphql
              })])
          },
          // Signiant Connectors, both directions. Portal session status and
          // import processes change out from under us, so this client uses a
          // plain cache and the components fetch network-only.
          'signiant': {
            cache: new InMemoryCache(),
            devtools: { enabled: true },
            link: ApolloLink.from([
              authMiddleware,
              httpLink.create({
                uri: environment.signiant_graphql
              })])
          }
        }
      }
    ),
    provideOAuthClient({
      resourceServer: {
        sendAccessToken: false,
      }
    }),
    {
      provide: 'above_default',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowDefault'),
      deps: [OAuthService]
    },
    {
      provide: 'vod_default',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
    {
      provide: 'vod_repackaging',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
    {
      provide: 'vod_display_interactive',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
    {
      // Watermark-only passthrough delivery. The action is the one the mutation is actually
      // authorized under -- it decides which folders may be added as items -- while the tab itself is
      // gated on `singleframewatermarking:Demo`.
      provide: 'vod_passthrough_sffw',
      useFactory: (oAuthService: OAuthService) =>
        new Po2024Service(oAuthService, 'contentplatform:workflow:Mutation/StartWorkflowVodDefault'),
      deps: [OAuthService]
    },
  ]
};
